import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { createZitadelAuth, ZitadelAuth } from '@zitadel/react';
import { useNavigate, useLocation } from 'react-router-dom';

// -------------------
// Define types
// -------------------

interface UserProfile {
  name?: string;
  given_name?: string;
  family_name?: string;
  email?: string;
  roles?: string[];
  [key: string]: any;
}

interface UserInfo {
  fullName: string;
  email: string;
  roles: string[];
  jwt: string;
  claims: UserProfile;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: UserProfile | null;
  userInfo: UserInfo | null;
  hasAddressRole: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
}

interface AuthProviderProps {
  children: ReactNode;
}

// -------------------
// Create context
// -------------------

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// -------------------
// AuthProvider
// -------------------

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authClient, setAuthClient] = useState<ZitadelAuth | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [hasAddressRole, setHasAddressRole] = useState<boolean>(false);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const client = createZitadelAuth({
      authority: process.env.REACT_APP_ZITADEL_AUTHORITY!,
      client_id: process.env.REACT_APP_ZITADEL_CLIENT_ID!,
      redirect_uri: window.location.origin + '/login/callback',
      post_logout_redirect_uri: window.location.origin + '/login',
      response_type: 'code',
      scope: 'openid profile email',
    });

    setAuthClient(client);

    const checkAuth = () => {
      const idTokenKey = `oidc.user:${process.env.REACT_APP_ZITADEL_AUTHORITY}:${process.env.REACT_APP_ZITADEL_CLIENT_ID}`;
      const idToken = window.localStorage.getItem(idTokenKey);

      if (idToken) {
        try {
          const parsed = JSON.parse(idToken);
          const profile: UserProfile = parsed.profile || {};

          setUser(profile);

          const userDetails: UserInfo = {
            fullName:
              profile.name ||
              `${profile.given_name || ''} ${profile.family_name || ''}`.trim(),
            email: profile.email || '',
            roles: profile.roles || [],
            jwt: parsed.id_token || '',
            claims: profile,
          };

          setUserInfo(userDetails);

          const rolesNew: string[] = profile['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];

          if (rolesNew && rolesNew.includes('BtAddressApp')) {
            setIsAuthenticated(true);
            setHasAddressRole(true);
            if (location.pathname === '/login' || location.pathname === '/login/callback') {
              navigate('/');
            }
          } else {
            setIsAuthenticated(true);
            setHasAddressRole(false);
            if (location.pathname !== '/not-authorized') {
              navigate('/not-authorized');
            }
          }
        } catch (error) {
          console.error('Error parsing user info:', error);
          setUser(null);
          setUserInfo(null);
          setIsAuthenticated(false);
          setHasAddressRole(false);
        }
      } else {
        setIsAuthenticated(false);
        setUser(null);
        setUserInfo(null);
        setHasAddressRole(false);
      }
    };

    checkAuth();
    window.addEventListener('storage', checkAuth);

    return () => {
      window.removeEventListener('storage', checkAuth);
    };
  }, [navigate, location]);

  const login = async (): Promise<void> => {
    try {
      await authClient?.authorize();
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await authClient?.signout();
      setIsAuthenticated(false);
      setUser(null);
      setUserInfo(null);
      setHasAddressRole(false);
      console.log('User logged out');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, user, userInfo, hasAddressRole, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// -------------------
// useAuth hook
// -------------------

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
